// Edit Lender Dialog Component
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useLenders } from '../../hooks/useLenders';
import { Lender } from '../../types';

interface EditLenderDialogProps {
  open: boolean;
  lender: Lender | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditLenderDialog: React.FC<EditLenderDialogProps> = ({ open, lender, onClose, onSuccess }) => {
  const { editLender } = useLenders();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    idNumber: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    bankCode: '',
    branchCode: '',
  });

  // Load lender data when dialog opens
  useEffect(() => {
    if (lender) {
      setFormData({
        fullName: lender.fullName || '',
        email: lender.email || '',
        phone: lender.phone || '',
        address: lender.address || '',
        idNumber: lender.idNumber || '',
        bankName: lender.bankDetails?.bankName || '',
        accountName: lender.bankDetails?.accountName || '',
        accountNumber: lender.bankDetails?.accountNumber || '',
        bankCode: lender.bankDetails?.bankCode || '',
        branchCode: lender.bankDetails?.branchCode || '',
      });
    }
  }, [lender]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Valid email is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!lender) return;
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      await editLender(lender.id, {
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        idNumber: formData.idNumber.trim(),
        bankDetails: {
          bankName: formData.bankName.trim(),
          accountName: formData.accountName.trim(),
          accountNumber: formData.accountNumber.trim(),
          bankCode: formData.bankCode.trim(),
          branchCode: formData.branchCode.trim(),
        },
        updatedAt: new Date(),
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update lender');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '1px solid #FFD700',
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #333' }}>
        Edit Lender
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mt: 1 }}>
            Personal Information
          </Typography>

          <TextField
            required
            fullWidth
            label="Full Name"
            value={formData.fullName}
            onChange={handleChange('fullName')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            required
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            required
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
            placeholder="+852 XXXX XXXX"
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            multiline
            rows={2}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="ID/Passport Number"
            value={formData.idNumber}
            onChange={handleChange('idNumber')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <Typography variant="subtitle2" sx={{ color: '#FFD700', mt: 2 }}>
            Bank Details
          </Typography>

          <TextField
            fullWidth
            label="Bank Name"
            value={formData.bankName}
            onChange={handleChange('bankName')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="Account Name"
            value={formData.accountName}
            onChange={handleChange('accountName')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="Account Number"
            value={formData.accountNumber}
            onChange={handleChange('accountNumber')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Bank Code"
              value={formData.bankCode}
              onChange={handleChange('bankCode')}
              disabled={loading}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />

            <TextField
              fullWidth
              label="Branch Code"
              value={formData.branchCode}
              onChange={handleChange('branchCode')}
              disabled={loading}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            bgcolor: '#FFD700',
            color: '#000',
            '&:hover': { bgcolor: '#FFC700' },
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLenderDialog;
